import { catAccentRGB } from "@/lib/format";

// SVG path `d` attributes (stroke-based, viewBox 0 0 24 24, strokeWidth 1.75).
export const ICONS: Record<string, string> = {
  calendario:
    "M8 7V3m8 4V3M3 11h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z",
  entrenamiento:
    "M13 10V3L4 14h7v7l9-11h-7z",
  salud:
    "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
  coach:
    "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  red:
    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  marketplace:
    "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z",
  notif:
    "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  ajustes:
    "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z",
  run:
    "M13.5 5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM7 18l1.75-5 2.25 2.5 3-6 2 4.5h3",
  bike:
    "M5 18a3 3 0 100-6 3 3 0 000 6zm14 0a3 3 0 100-6 3 3 0 000 6zM5 15h3l2-4h4l2.5-4 1.5 4H19",
  moon:
    "M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z",
  sun:
    "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
};

// Accent constants shared across nav and dashboard logic.
// Dashboard.tsx keeps its own copies until F2 migrates to these.
export const ACCENTS = {
  brand:        "252 76 2",
  carga:        "239 68 68",
  material:     "34 197 94",
  calendario:   "234 179 8",
  saludResumen: "20 184 166",
  cuerpo:       "16 185 129",
  sueno:        "99 102 241",
  coach:        "124 58 237",
  red:          "79 110 247",
} as const;

export type NavSub = {
  key: string;
  labelKey: string; // i18n key resolved by the consuming component via t()
  accent: string;   // RGB string, e.g. "252 76 2"
};

export type NavItem = {
  key: string;
  labelKey: string;
  icon: string;     // key into ICONS
  accent: string;   // accent used when this item or any sub is active
  subs?: NavSub[];
  badge?: number;
};

export type NavGroup = {
  key: string;
  labelKey: string;
  items: NavItem[];
};

export type NavPersona = "athlete" | "coach";

export type NavData = {
  disciplines?: string[]; // e.g. ["Carrera", "Ciclismo"]
  unreadCount?: number;   // badge count for "Mi entrenador"
};

export function buildNav(persona: NavPersona, data: NavData = {}): NavGroup[] {
  const { disciplines = [], unreadCount } = data;

  const discSubs: NavSub[] = disciplines.map((cat) => ({
    key: cat,
    labelKey: `cat_${cat}`,
    accent: catAccentRGB(cat),
  }));

  const principal: NavGroup = {
    key: "principal",
    labelKey: "nav_group_principal",
    items: [
      {
        key: "entrenamiento",
        labelKey: "nav_entrenamiento",
        icon: "entrenamiento",
        accent: ACCENTS.brand,
        subs: [
          { key: "resumen",  labelKey: "tab_resumen",  accent: ACCENTS.brand },
          ...discSubs,
          { key: "carga",    labelKey: "tab_carga",    accent: ACCENTS.carga },
          { key: "material", labelKey: "tab_material", accent: ACCENTS.material },
        ],
      },
      {
        key: "salud",
        labelKey: "seg_salud",
        icon: "salud",
        accent: ACCENTS.saludResumen,
        subs: [
          { key: "s_resumen", labelKey: "tab_s_resumen", accent: ACCENTS.saludResumen },
          { key: "s_cuerpo",  labelKey: "tab_s_cuerpo",  accent: ACCENTS.cuerpo },
          { key: "s_sueno",   labelKey: "tab_s_sueno",   accent: ACCENTS.sueno },
          { key: "s_cruce",   labelKey: "tab_s_cruce",   accent: ACCENTS.coach },
        ],
      },
      {
        key: "calendario",
        labelKey: "tab_calendario",
        icon: "calendario",
        accent: ACCENTS.calendario,
      },
      {
        key: "coach",
        labelKey: "seg_coach",
        icon: "coach",
        accent: ACCENTS.coach,
      },
    ],
  };

  const redItems: NavItem[] =
    persona === "athlete"
      ? [
          {
            key: "red",
            labelKey: "nav_red_athlete",
            icon: "red",
            accent: ACCENTS.red,
            badge: unreadCount ?? 0,
            subs: [
              { key: "entrenador", labelKey: "nav_red_entrenador", accent: ACCENTS.red },
            ],
          },
        ]
      : [
          {
            key: "red",
            labelKey: "nav_red_coach_zone",
            icon: "red",
            accent: ACCENTS.red,
            subs: [
              { key: "atletas",  labelKey: "coach_subtab_athletes", accent: ACCENTS.red },
              { key: "mensajes", labelKey: "nav_red_messages",      accent: ACCENTS.red },
              { key: "codigos",  labelKey: "nav_red_codes",         accent: ACCENTS.red },
            ],
          },
        ];

  return [
    principal,
    { key: "red", labelKey: "nav_group_red", items: redItems },
  ];
}
